
'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { flexRender } from '@tanstack/react-table';
import { useDataGrid } from './hooks/useDataGrid';
import { useGrouping } from './hooks/useGrouping';
import { TableControls } from './components/TableControls';
import { TablePagination } from './components/TablePagination';
import { SummaryRow } from './components/SummaryRow';
import { GroupingArea } from './components/GroupingArea';
import { CardView } from './views/CardView';
import { ListView } from './views/ListView';
import { ScrumboardView } from './views/ScrumboardView';
import { AnalyticsChartsView } from './views/AnalyticsChartsView';
import { AdvancedFilter } from './components/AdvancedFilter';
import { FilterDropdown } from './components/FilterDropdown';
import { getTableClasses, getHeaderCustomStyles, getHeaderClassesWithCustomColors } from './utils/cssHelper';
import { createMultiLevelHeaderStructure, getGroupColumnSpan } from './utils/columnGroupingUtils';
import { createGroupedColumnStructure, getGroupColumnSpan as getNewGroupColumnSpan } from './utils/columnGroupingManager';
import type { DataGridProps } from './types';

export const DataGrid: React.FC<DataGridProps> = (props) => {
  const [groupedColumns, setGroupedColumns] = useState<string[]>([]);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [hasUserInteractedWithGrouping, setHasUserInteractedWithGrouping] = useState(false);


  // Flatten columns structure for TanStack Table
  const flattenColumns = (columns: any[]) => {
    const flattened: any[] = [];
    columns.forEach(col => {
      if (col.subColumns && col.subColumns.length > 0) {
        // Add subColumns to flattened array (they already have all properties preserved)
        col.subColumns.forEach((subCol: any) => {
          flattened.push(subCol);
        });
      } else {
        // Add regular column
        flattened.push(col);
      }
    });
    return flattened;
  };

  // Helper function to detect if a column is a serial number column
  const isSerialNumberColumn = (columnName: string, field: string, columnIndex: number): boolean => {
    if (columnIndex !== 0) return false; // Only check first column
    const serialPatterns = [
      /^s\.?no\.?$/i,
      /^sr\.?no\.?$/i,
      /^sl\.?no\.?$/i,
      /^serial\.?(no\.?|number)?$/i,
      /^#$/,
      /^no\.?$/i,
      /^sno$/i,
      /^srno$/i,
      /^slno$/i,
      /^id$/i,
    ];
    const nameToCheck = (columnName || field || '').trim();
    return serialPatterns.some(pattern => pattern.test(nameToCheck));
  };

  // Helper function to detect if a column is an actions column
  const isActionsColumn = (columnName: string, field: string): boolean => {
    const actionsPatterns = [
      /^actions?$/i,
      /^operations?$/i,
      /^commands?$/i,
    ];
    const nameToCheck = (columnName || field || '').trim();
    return actionsPatterns.some(pattern => pattern.test(nameToCheck));
  };

  // Serial number column styling
  const SERIAL_COL_WIDTH = 60;
  const getSerialColumnStyle = (isSerial: boolean) => isSerial ? {
    width: SERIAL_COL_WIDTH,
    minWidth: SERIAL_COL_WIDTH,
    maxWidth: SERIAL_COL_WIDTH,
    textAlign: 'center' as const,
  } : {};

  // Actions column styling - fit content
  const ACTIONS_COL_WIDTH = 100;
  const getActionsColumnStyle = (isActions: boolean) => isActions ? {
    width: ACTIONS_COL_WIDTH,
    minWidth: ACTIONS_COL_WIDTH,
    maxWidth: ACTIONS_COL_WIDTH,
    textAlign: 'center' as const,
  } : {};

  // Build hierarchical structure if parentId is used so the header renders multi-level properly
  const effectiveColumns = useMemo(() => {
    if (!props.columns || props.columns.length === 0) return [];
    
    // If already nested, do not over-process
    if (props.columns.some((c: any) => c.subColumns && c.subColumns.length > 0)) {
      return props.columns;
    }

    // Clone columns to avoid mutation, ensuring default empty subColumns array
    const cols = props.columns.map((c: any) => ({ ...c, subColumns: [] }));
    const colMap = new Map();
    
    // First pass: Build reference map
    cols.forEach((c: any) => {
      const colKey = c.field || c.columnName;
      if (colKey) colMap.set(String(colKey).trim(), c);
    });

    let hasBuiltTree = false;
    const roots: any[] = [];
    
    // Second pass: Assemble tree
    cols.forEach((c: any) => {
      const pId = c.parentId ? String(c.parentId).trim() : '';
      if (pId && pId !== 'None' && colMap.has(pId)) {
        colMap.get(pId).subColumns.push(c);
        hasBuiltTree = true;
      } else {
        roots.push(c);
      }
    });

    return hasBuiltTree ? roots : props.columns;
  }, [props.columns]);

  const flattenedColumns = useMemo(() => flattenColumns(effectiveColumns), [effectiveColumns]);

  const {
    table,
    globalFilter,
    setGlobalFilter,
    setColumnVisibility,
    setColumnFilter,
  } = useDataGrid({
    ...props,
    columns: flattenedColumns,
    onPaginationChange: props.onPaginationChange,
    manualPagination: props.manualPagination,
    pageCount: props.pageCount,
  });

  const {
    groupedData,
    expandAllGroups,
    collapseAllGroups,
    renderGroupedRows,
  } = useGrouping(props.dataSource, groupedColumns, flattenedColumns);

  // Initialize default grouping when grid loads
  useEffect(() => {
    const { gridConfig } = props;
    if (gridConfig.enableDefaultGrouping && gridConfig.defaultGroupColumn &&
      groupedColumns.length === 0 && !hasUserInteractedWithGrouping) {
      // Check if the default group column exists and is groupable
      const columnConfig = flattenedColumns.find(
        (col) => (col.field || col.columnName) === gridConfig.defaultGroupColumn
      );

      if (columnConfig && columnConfig.allowGrouping !== false && columnConfig.field) {
        setGroupedColumns([gridConfig.defaultGroupColumn]);
      }
    }
  }, [props.gridConfig.enableDefaultGrouping, props.gridConfig.defaultGroupColumn, flattenedColumns, groupedColumns.length, hasUserInteractedWithGrouping]);

  // Handle expand groups on load
  useEffect(() => {
    const { gridConfig } = props;
    if (gridConfig.expandGroupsOnLoad && groupedColumns.length > 0) {
      // Small delay to ensure groupedData is available
      setTimeout(() => {
        expandAllGroups();
      }, 100);
    }
  }, [groupedColumns.length, props.gridConfig.expandGroupsOnLoad, expandAllGroups]);

  // Reset user interaction flag when default grouping config changes
  useEffect(() => {
    setHasUserInteractedWithGrouping(false);
  }, [props.gridConfig.enableDefaultGrouping, props.gridConfig.defaultGroupColumn]);

  const { dataSource, gridConfig, viewConfig } = props;
  // Use effectiveColumns for rendering
  const columns = effectiveColumns;

  // Smart filter helper - determine if column should use dropdown based on distinct values
  const SMART_FILTER_THRESHOLD = 20; // Use dropdown if distinct values <= this
  const getDistinctValuesCount = (field: string): number => {
    if (!field || !dataSource || dataSource.length === 0) return Infinity;
    const uniqueValues = new Set(dataSource.map(row => row[field]).filter(v => v !== null && v !== undefined));
    return uniqueValues.size;
  };

  const shouldUseDropdownFilter = (field: string): boolean => {
    const distinctCount = getDistinctValuesCount(field);
    return distinctCount > 0 && distinctCount <= SMART_FILTER_THRESHOLD;
  };

  // Custom handler for grouping changes that tracks user interaction
  const handleGroupingChange = (newGroupedColumns: string[]) => {
    setHasUserInteractedWithGrouping(true);
    setGroupedColumns(newGroupedColumns);
  };

  const handleColumnDragStart = (e: React.DragEvent, columnField: string) => {
    const columnConfig = flattenedColumns.find((col) => (col.field || col.columnName) === columnField);
    const isGroupable = columnConfig?.allowGrouping !== false && columnConfig?.field && !groupedColumns.includes(columnConfig.field);

    setDraggedColumn(columnField);
    e.dataTransfer.setData('text/plain', columnField);

    // Set different effects based on what's enabled and allowed
    if (gridConfig.columnReordering && !isGroupable) {
      // Only reordering allowed
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('action', 'reorder');
    } else if (gridConfig.columnReordering && isGroupable) {
      // Both reordering and grouping allowed
      e.dataTransfer.effectAllowed = 'copyMove';
      e.dataTransfer.setData('action', 'both');
    } else if (isGroupable) {
      // Only grouping allowed
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData('action', 'group');
    } else {
      // Nothing allowed, prevent drag
      e.preventDefault();
      return;
    }
  };

  const handleGroupingDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const columnField = e.dataTransfer.getData('text/plain');
    const action = e.dataTransfer.getData('action');

    // Only allow grouping if the action allows it
    if (columnField && !groupedColumns.includes(columnField) && (action === 'group' || action === 'both')) {
      const columnConfig = flattenedColumns.find((col) => (col.field || col.columnName) === columnField);
      const isGroupable = columnConfig?.allowGrouping !== false && columnConfig?.field;

      if (isGroupable) {
        handleGroupingChange([...groupedColumns, columnField]);
      }
    }
    setDraggedColumn(null);
  };

  const handleGroupingDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // Column reordering handlers
  const handleColumnDragOver = (e: React.DragEvent) => {
    if (!gridConfig.columnReordering) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleColumnDrop = (e: React.DragEvent, targetColumnId: string) => {
    if (!gridConfig.columnReordering) return;
    e.preventDefault();

    const draggedColumnId = e.dataTransfer.getData('text/plain');
    if (draggedColumnId && draggedColumnId !== targetColumnId) {
      // For multi-level headers, we need to work with the original columns structure
      // Find the dragged and target columns in the original columns array
      let draggedColumn = null;
      let targetColumn = null;
      let draggedParentIndex = -1;
      let targetParentIndex = -1;

      // Find dragged column and its parent
      for (let i = 0; i < props.columns.length; i++) {
        const col = props.columns[i];
        if (col.subColumns) {
          const subColIndex = col.subColumns.findIndex(subCol => subCol.field === draggedColumnId);
          if (subColIndex !== -1) {
            draggedColumn = col.subColumns[subColIndex];
            draggedParentIndex = i;
            break;
          }
        } else if (col.field === draggedColumnId) {
          draggedColumn = col;
          draggedParentIndex = i;
          break;
        }
      }

      // Find target column and its parent
      for (let i = 0; i < props.columns.length; i++) {
        const col = props.columns[i];
        if (col.subColumns) {
          const subColIndex = col.subColumns.findIndex(subCol => subCol.field === targetColumnId);
          if (subColIndex !== -1) {
            targetColumn = col.subColumns[subColIndex];
            targetParentIndex = i;
            break;
          }
        } else if (col.field === targetColumnId) {
          targetColumn = col;
          targetParentIndex = i;
          break;
        }
      }

      if (draggedColumn && targetColumn && draggedParentIndex !== -1 && targetParentIndex !== -1) {
        // Create a new columns array with reordered columns
        const newColumns = [...props.columns];

        // Remove dragged column from its current position
        if (newColumns[draggedParentIndex]?.subColumns) {
          newColumns[draggedParentIndex].subColumns = newColumns[draggedParentIndex].subColumns!.filter(
            subCol => subCol.field !== draggedColumnId
          );
        } else {
          newColumns.splice(draggedParentIndex, 1);
        }

        // Insert dragged column at target position
        if (newColumns[targetParentIndex]?.subColumns) {
          const targetSubIndex = newColumns[targetParentIndex].subColumns!.findIndex(
            subCol => subCol.field === targetColumnId
          );
          newColumns[targetParentIndex].subColumns!.splice(targetSubIndex, 0, draggedColumn as any);
        } else {
          newColumns.splice(targetParentIndex, 0, draggedColumn);
        }

        // Update the columns through the parent component
        if (props.onColumnsChange) {
          props.onColumnsChange(newColumns);
        }
      }
    }
    setDraggedColumn(null);
  };



  const renderView = () => {
    const viewFormat = viewConfig?.format || 'table';

    switch (viewFormat) {
      case 'card':
        return (
          <CardView
            {...props}
            titleColumn={viewConfig?.cardConfig?.titleColumn || ''}
            visibleColumns={viewConfig?.cardConfig?.visibleColumns || []}
            layout={viewConfig?.cardConfig?.layout || {}}
            onTitleColumnChange={() => { }} // No longer needed as handled by ViewSwitcher
            onViewChange={() => { }} // No longer needed as handled by ViewSwitcher
          />
        );
      case 'list':
        return (
          <ListView
            {...props}
            titleColumn={viewConfig?.listConfig?.titleColumn || ''}
            visibleColumns={viewConfig?.listConfig?.visibleColumns || []}
            layout={viewConfig?.listConfig?.layout || {}}
            onTitleColumnChange={() => { }} // No longer needed as handled by ViewSwitcher
            onViewChange={() => { }} // No longer needed as handled by ViewSwitcher
          />
        );
      case 'scrumboard':
        return (
          <ScrumboardView
            data={props.dataSource}
            columns={props.columns}
            selectedColumn={viewConfig?.scrumboardColumn || ''}
            onColumnChange={() => { }} // No longer needed as handled by ViewSwitcher
            onCardMove={(cardId: string, newStatus: string) => {
              // console.log('Card moved:', cardId, 'to:', newStatus);
            }}
          />
        );
      case 'analytics_charts':
        return (
          <AnalyticsChartsView
            {...props}
            config={viewConfig?.analyticsConfig || {
              xAxis: '',
              yAxis: '',
              chartType: 'bar',
              operation: 'sum'
            }}
            onConfigChange={() => { }} // No longer needed as handled by ViewSwitcher
          />
        );
      default:
        return renderTableView();
    }
  };

  const renderTableView = () => {
    // Get CSS classes from configuration
    const tableClasses = getTableClasses(gridConfig);
    const customStyles = getHeaderCustomStyles(gridConfig.headerCustomization || {});

    return (
      <div className="h-full flex-1 flex flex-col min-h-0" style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', overflowY: 'hidden' }}>
        {/* Grouping Area */}
        {gridConfig.enableGroupingPanel && (
          <div className="flex-shrink-0">
            <GroupingArea
              columns={columns}
              groupedColumns={groupedColumns}
              onGroupChange={handleGroupingChange}
              onDragOver={handleGroupingDragOver}
              onDrop={handleGroupingDrop}
            />
          </div>
        )}

        {/* Group Controls */}
        {gridConfig.enableGroupingPanel && groupedColumns.length > 0 && (
          <div className="flex items-center gap-2 mb-4 flex-shrink-0">
            <button
              onClick={expandAllGroups}
              className="flex items-center px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm hover:bg-gray-200 dark:hover:bg-gray-600 whitespace-nowrap"
            >
              <i className="ri-add-box-line mr-1 w-4 h-4 flex items-center justify-center"></i>
              Expand All
            </button>
            <button
              onClick={collapseAllGroups}
              className="flex items-center px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm hover:bg-gray-200 dark:hover:bg-gray-600 whitespace-nowrap"
            >
              <i className="ri-subtract-line mr-1 w-4 h-4 flex items-center justify-center"></i>
              Collapse All
            </button>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Grouped by {groupedColumns.length} column{groupedColumns.length > 1 ? 's' : ''}
            </div>
          </div>
        )}

        {/* Responsive Table Container */}
        {/*
          `ds-grid-surface` is a hook, not a style — the utility classes beside
          it are unchanged and a standalone grid still renders exactly as before.
          It exists so a host that already draws a card of its own can switch
          this one off, instead of nesting a bordered, shadowed white box inside
          a bordered, shadowed white box.
        */}
        <div className="ds-grid-surface border border-gray-200 dark:border-gray-600 rounded-lg shadow-sm bg-white dark:bg-gray-900 flex flex-col flex-1 min-h-0" style={{ width: '100%', maxWidth: '100%' }}>
          {/* Table Controls - Fixed at top */}
          <div className="flex-shrink-0 relative" style={{ overflow: 'visible' }}>
            <TableControls
              table={table}
              gridConfig={gridConfig}
              globalFilter={props.onServerSearch ? (props.serverSearchValue || '') : globalFilter}
              setGlobalFilter={props.onServerSearch ? props.onServerSearch : setGlobalFilter}
              setColumnVisibility={setColumnVisibility}
              isServerSideSearch={!!props.onServerSearch}
              exportFetchAll={props.exportFetchAll}
              columns={flattenedColumns}
              toolbarStart={props.toolbarStart}
              toolbarEnd={props.toolbarEnd}
            />
          </div>
          {/* Scrollable Table Area - Horizontal and vertical scroll contained */}
          <div
            style={{
              flex: '1 1 0%',
              width: 0,
              minWidth: '100%',
              maxWidth: '100%',
              overflowX: 'auto',
              overflowY: 'auto',
              minHeight: 0,
              maxHeight: '100%',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <table
              style={{
                minWidth: '100%',
                width: gridConfig.enableColumnResizing ? table.getCenterTotalSize() : '100%',
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                ...customStyles,
              }}
              className={tableClasses.table}
            >
              <thead
                className={tableClasses.thead}
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 20,
                  ...(gridConfig.headerCustomization?.headerBackgroundColor ? { backgroundColor: gridConfig.headerCustomization.headerBackgroundColor } : {})
                }}
              >
                {(() => {
                  // Check if any column has subColumns for multi-level headers
                  const hasSubColumns = columns.some(col => col.subColumns && col.subColumns.length > 0);

                  if (hasSubColumns) {
                    // Manual header rendering for nested column structure
                    // Get all headers, handle selection column separately
                    const allHeaders = table.getHeaderGroups()[0].headers;
                    const selectionHeader = allHeaders.find(h => h.column.id === 'select') || null;
                    const dataHeaders = allHeaders.filter(h => h.column.id !== 'select');
                    // Build a stable map of columnId -> display index (1-based)
                    const headerIndexMap = new Map<string, number>();
                    dataHeaders.forEach((h, idx) => headerIndexMap.set(h.column.id, idx + 1));

                    // Build header structure from columns
                    const headerStructure: any[] = [];

                    // Visibility helpers
                    const leafKey = (c: any) => c.field || c.columnName; // stable key used across maps
                    const isVisible = (c: any) => c.visible !== false;
                    const getVisibleSubColumns = (c: any) => (c.subColumns || []).filter((sc: any) => isVisible(sc));
                    const countVisibleLeaves = (c: any): number => {
                      if (c.subColumns && c.subColumns.length > 0) {
                        return getVisibleSubColumns(c).reduce((sum: number, sc: any) => sum + countVisibleLeaves(sc), 0);
                      }
                      return isVisible(c) ? 1 : 0;
                    };

                    // Also build a fallback index order purely from visible leaf columns
                    const leafOrderMap = new Map<string, number>();
                    let leafIdxCounter = 1;
                    const buildLeafOrder = (c: any) => {
                      if (c.subColumns && c.subColumns.length > 0) {
                        getVisibleSubColumns(c).forEach((sc: any) => buildLeafOrder(sc));
                      } else if (isVisible(c)) {
                        const key = leafKey(c);
                        if (!leafOrderMap.has(key)) {
                          leafOrderMap.set(key, leafIdxCounter++);
                        }
                      }
                    };

                    columns.forEach(col => buildLeafOrder(col));

                    columns.forEach(col => {
                      // Skip completely hidden columns/groups
                      const visibleSub = getVisibleSubColumns(col);
                      if (col.subColumns && col.subColumns.length > 0) {
                        // Check if any subColumn has its own subColumns (3-level structure)
                        const hasNestedSubColumns = visibleSub.some((subCol: any) => subCol.subColumns && getVisibleSubColumns(subCol).length > 0);

                        if (hasNestedSubColumns) {
                          // 3-level structure: main group -> subgroup -> columns
                          headerStructure.push({
                            type: 'mainGroup',
                            column: col,
                            subGroups: visibleSub.map((subCol: any) => ({
                              type: 'subGroup',
                              column: subCol,
                              columns: getVisibleSubColumns(subCol).length > 0 ? getVisibleSubColumns(subCol) : [subCol]
                            }))
                          });
                        } else {
                          // 2-level structure: group -> columns
                          headerStructure.push({
                            type: 'group',
                            column: col,
                            columns: visibleSub
                          });
                        }
                      } else {
                        // Single column
                        if (isVisible(col)) {
                          headerStructure.push({
                            type: 'single',
                            column: col
                          });
                        }
                      }
                    });

                    // Check if we have 3-level structure
                    const hasThreeLevels = headerStructure.some(item => item.type === 'mainGroup');
                    const maxLevels = hasThreeLevels ? 3 : 2;

                    // Default group header colors — cycle through these when column_css_class is not provided
                    const DEFAULT_GROUP_COLORS = [
                      { header: '!bg-teal-100 dark:!bg-teal-900/50 !text-teal-800 dark:!text-teal-200', sub: '!bg-teal-50 dark:!bg-teal-900/30' },
                      { header: '!bg-amber-100 dark:!bg-amber-900/50 !text-amber-800 dark:!text-amber-200', sub: '!bg-amber-50 dark:!bg-amber-900/30' },
                      { header: '!bg-purple-100 dark:!bg-purple-900/50 !text-purple-800 dark:!text-purple-200', sub: '!bg-purple-50 dark:!bg-purple-900/30' },
                    ];
                    const groupColorMap = new Map<number, number>();
                    let groupColorCounter = 0;
                    headerStructure.forEach((item, idx) => {
                      if (item.type === 'group' || item.type === 'mainGroup') {
                        groupColorMap.set(idx, groupColorCounter++);
                      }
                    });
                    const getGroupColor = (idx: number, type: 'header' | 'sub') => {
                      const gi = groupColorMap.get(idx);
                      if (gi === undefined) return '';
                      return DEFAULT_GROUP_COLORS[gi % DEFAULT_GROUP_COLORS.length][type];
                    };

                    const rows = [];

                    // Level 1: Main groups (only if 3-level structure)
                    if (hasThreeLevels) {
                      rows.push(
                        <tr key="level-1" className={tableClasses.headerRow}>
                          {selectionHeader && (
                            <th
                              key="select-main"
                              rowSpan={maxLevels}
                              className={`${getHeaderClassesWithCustomColors(tableClasses.headerCell, gridConfig.headerCustomization || {})} cursor-pointer text-center`}
                              style={{
                                width: selectionHeader.getSize(),
                                minWidth: 50,
                                maxWidth: gridConfig.enableColumnResizing ? selectionHeader.getSize() : undefined,
                              }}
                            >
                              {flexRender(selectionHeader.column.columnDef.header, selectionHeader.getContext())}
                            </th>
                          )}
                          {headerStructure.map((item, index) => {
                            if (item.type === 'mainGroup') {
                              const totalCols = item.subGroups.reduce((sum: number, subGroup: any) => sum + countVisibleLeaves(subGroup.column), 0);
                              return (
                                <th
                                  key={`main-${index}`}
                                  colSpan={totalCols}
                                  className={`${getHeaderClassesWithCustomColors(tableClasses.groupHeaderCell || tableClasses.headerCell, gridConfig.headerCustomization || {})} text-center font-bold bg-gradient-to-r from-indigo-100 via-purple-100 to-pink-100 dark:from-indigo-900 dark:via-purple-900 dark:to-pink-900 text-gray-800 dark:text-gray-100`}
                                  style={{ minWidth: '120px' }}
                                >
                                  <span className="whitespace-normal break-words leading-tight">
                                    {item.column.columnName}
                                  </span>
                                </th>
                              );
                            } else {
                              // Single columns get rowSpan=3
                              const singleKey = leafKey(item.column);
                              const header = dataHeaders.find(h => h.column.id === singleKey);
                              if (!header) return null;

                              const columnConfig = flattenedColumns.find(f => (f.field || f.columnName) === singleKey);
                              const isGroupable = columnConfig?.allowGrouping !== false && columnConfig?.field && !groupedColumns.includes(columnConfig.field);
                              const isReorderable = gridConfig.columnReordering && columnConfig?.field;
                              const isDraggable = isGroupable || isReorderable;

                              return (
                                <th
                                  key={`single-main-${index}`}
                                  rowSpan={3}
                                  className={`${getHeaderClassesWithCustomColors(tableClasses.headerCell, gridConfig.headerCustomization || {})} ${(isDraggable || header?.column.getCanSort()) ? 'cursor-pointer' : ''
                                    } ${isDraggable ? 'draggable-column' : ''
                                    } ${gridConfig.enableColumnResizing ? 'relative overflow-visible' : ''} ${draggedColumn === item.column.field ? 'opacity-50' : ''
                                    }`}
                                  style={{
                                    width: header.getSize(),
                                    minWidth: Math.max(header.getSize(), 120),
                                    maxWidth: gridConfig.enableColumnResizing ? header.getSize() : undefined,
                                  }}
                                  draggable={!!isDraggable}
                                  onDragStart={(e) => columnConfig?.field && handleColumnDragStart(e, columnConfig.field)}
                                  onDragOver={gridConfig.columnReordering ? handleColumnDragOver : undefined}
                                  onDrop={gridConfig.columnReordering ? (e) => handleColumnDrop(e, item.column.field) : undefined}
                                  onClick={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const clickX = e.clientX;
                                    const rightEdge = rect.right;
                                    const isResizeArea = clickX > rightEdge - 10;
                                    if (!isResizeArea) {
                                      if (header?.column.getCanSort()) {
                                        const sortHandler = header.column.getToggleSortingHandler();
                                        if (sortHandler) sortHandler(e);
                                      }
                                    }
                                  }}
                                >
                                  <div className="flex flex-col items-center justify-center min-w-0">
                                    <div className="flex items-center justify-center w-full">
                                      <span className="whitespace-normal break-words leading-tight">
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                      </span>
                                      {columnConfig?.columnType !== 'button' && (
                                        <div className="ml-1 flex-shrink-0">
                                          <i className="ri-expand-up-down-line w-3 h-3 flex items-center justify-center text-gray-400"></i>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {gridConfig.enableColumnNumbering && (
                                    <div className="mt-1">
                                      <span className="inline-flex items-center justify-center w-4 h-4 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs font-bold rounded-full">
                                        {leafOrderMap.get(singleKey) ?? 0}
                                      </span>
                                    </div>
                                  )}
                                  {gridConfig.enableColumnResizing && header && (
                                    <div
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                      }}
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        const resizeHandler = header.getResizeHandler();
                                        if (resizeHandler) resizeHandler(e);
                                      }}
                                      onTouchStart={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        const resizeHandler = header.getResizeHandler();
                                        if (resizeHandler) resizeHandler(e);
                                      }}
                                      className={`absolute -right-1 top-0 w-3 cursor-col-resize select-none touch-none hover:bg-blue-500 transition-colors duration-200 ${header.column.getIsResizing() ? 'bg-blue-500' : 'bg-transparent hover:bg-blue-300'
                                        }`}
                                      style={{
                                        height: '100%',
                                        zIndex: 20,
                                        borderRight: '2px solid transparent',
                                      }}
                                      onMouseEnter={(e) => {
                                        (e.currentTarget as HTMLElement).style.borderRight = '2px solid #3b82f6';
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!header.column.getIsResizing()) {
                                          (e.currentTarget as HTMLElement).style.borderRight = '2px solid transparent';
                                        }
                                      }}
                                      title="Drag to resize column"
                                    />
                                  )}
                                </th>
                              );
                            }
                          })}
                        </tr>
                      );
                    }

                    // Level 2: Subgroups or Groups
                    rows.push(
                      <tr key="level-2" className={tableClasses.headerRow}>
                        {!hasThreeLevels && selectionHeader && (
                          <th
                            key="select-sub"
                            rowSpan={2}
                            className={`${getHeaderClassesWithCustomColors(tableClasses.headerCell, gridConfig.headerCustomization || {})} cursor-pointer text-center`}
                            style={{
                              width: selectionHeader.getSize(),
                              minWidth: 50,
                              maxWidth: gridConfig.enableColumnResizing ? selectionHeader.getSize() : undefined,
                            }}
                          >
                            {flexRender(selectionHeader.column.columnDef.header, selectionHeader.getContext())}
                          </th>
                        )}
                        {headerStructure.map((item, index) => {
                          if (item.type === 'mainGroup') {
                            // Render subgroups; if a subgroup is just a single leaf column,
                            // render the column header here with rowSpan=2 to avoid duplicate labels
                            return item.subGroups.map((subGroup: any, subIndex: number) => {
                              const isSingleLeaf = subGroup.columns.length === 1 && !subGroup.columns[0]?.subColumns;
                              if (isSingleLeaf) {
                                const leaf = subGroup.columns[0];
                                if (leaf.visible === false) return null;
                                const key = leafKey(leaf);
                                const header = dataHeaders.find(h => h.column.id === key);
                                const columnConfig = flattenedColumns.find(f => (f.field || f.columnName) === key);
                                const isGroupable = columnConfig?.allowGrouping !== false && columnConfig?.field && !groupedColumns.includes(columnConfig.field);
                                const isReorderable = gridConfig.columnReordering && columnConfig?.field;
                                const isDraggable = isGroupable || isReorderable;

                                return (
                                  <th
                                    key={`single-under-main-${index}-${subIndex}`}
                                    rowSpan={2}
                                    className={`${getHeaderClassesWithCustomColors(tableClasses.headerCell, gridConfig.headerCustomization || {})} ${(isDraggable || header?.column.getCanSort()) ? 'cursor-pointer' : ''
                                      } ${isDraggable ? 'draggable-column' : ''
                                      } ${gridConfig.enableColumnResizing ? 'relative overflow-visible' : ''} ${draggedColumn === leaf.field ? 'opacity-50' : ''
                                      }`}
                                    style={{
                                      width: header?.getSize() || 120,
                                      minWidth: Math.max(header?.getSize() || 120, 120),
                                      maxWidth: gridConfig.enableColumnResizing ? header?.getSize() : undefined,
                                    }}
                                    draggable={!!isDraggable}
                                    onDragStart={(e) => columnConfig?.field && handleColumnDragStart(e, columnConfig.field)}
                                    onDragOver={gridConfig.columnReordering ? handleColumnDragOver : undefined}
                                    onDrop={gridConfig.columnReordering ? (e) => handleColumnDrop(e, leaf.field) : undefined}
                                    onClick={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const clickX = e.clientX;
                                      const rightEdge = rect.right;
                                      const isResizeArea = clickX > rightEdge - 10;
                                      if (!isResizeArea && header?.column.getCanSort()) {
                                        const sortHandler = header.column.getToggleSortingHandler();
                                        if (sortHandler) sortHandler(e);
                                      }
                                    }}
                                  >
                                    <div className="flex flex-col items-center justify-center min-w-0">
                                      <div className="flex items-center justify-center w-full">
                                        <span className="whitespace-normal break-words leading-tight">
                                          {leaf.columnName || leaf.field}
                                        </span>
                                      </div>
                                    </div>
                                    {gridConfig.enableColumnNumbering && (
                                      <div className="mt-1">
                                        <span className="inline-flex items-center justify-center w-4 h-4 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs font-bold rounded-full">
                                          {leafOrderMap.get(key) ?? 0}
                                        </span>
                                      </div>
                                    )}
                                    {gridConfig.enableColumnResizing && header && (
                                      <div
                                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                                        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); const rh = header.getResizeHandler(); if (rh) rh(e); }}
                                        onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); const rh = header.getResizeHandler(); if (rh) rh(e); }}
                                        className={`absolute -right-1 top-0 w-3 cursor-col-resize select-none touch-none hover:bg-blue-500 transition-colors duration-200 ${header.column.getIsResizing() ? 'bg-blue-500' : 'bg-transparent hover:bg-blue-300'
                                          }`}
                                        style={{ height: '100%', zIndex: 20, borderRight: '2px solid transparent' }}
                                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderRight = '2px solid #3b82f6'; }}
                                        onMouseLeave={(e) => { if (!header.column.getIsResizing()) { (e.currentTarget as HTMLElement).style.borderRight = '2px solid transparent'; } }}
                                        title="Drag to resize column"
                                      />
                                    )}
                                  </th>
                                );
                              }

                              // Regular subgroup header
                              return (
                                <th
                                  key={`sub-${index}-${subIndex}`}
                                  colSpan={countVisibleLeaves(subGroup.column)}
                                  className={`${getHeaderClassesWithCustomColors(tableClasses.groupHeaderCell || tableClasses.headerCell, gridConfig.headerCustomization || {})} text-center font-semibold`}
                                  style={{ minWidth: '120px' }}
                                >
                                  <span className="whitespace-normal break-words leading-tight">
                                    {subGroup.column.columnName}
                                  </span>
                                </th>
                              );
                            });
                          } else if (item.type === 'group') {
                            // Regular group
                            return (
                              <th
                                key={`group-${index}`}
                                colSpan={item.columns.filter((c: any) => c.visible !== false).length}
                                className={`${getHeaderClassesWithCustomColors(tableClasses.groupHeaderCell || tableClasses.headerCell, gridConfig.headerCustomization || {})} text-center font-semibold ${item.column.column_css_class || getGroupColor(index, 'header')}`}
                                style={{ minWidth: '120px' }}
                              >
                                <span className="whitespace-normal break-words leading-tight">
                                  {item.column.columnName}
                                </span>
                              </th>
                            );
                          } else if (!hasThreeLevels) {
                            // Single column in 2-level structure
                            const singleKey = leafKey(item.column);
                            const header = dataHeaders.find(h => h.column.id === singleKey);
                            if (!header) return null;

                            const columnConfig = flattenedColumns.find(f => f.field === item.column.field);
                            const isGroupable = columnConfig?.allowGrouping !== false && columnConfig?.field && !groupedColumns.includes(columnConfig.field);
                            const isReorderable = gridConfig.columnReordering && columnConfig?.field;
                            const isDraggable = isGroupable || isReorderable;

                            return (
                              <th
                                key={`single-sub-${index}`}
                                rowSpan={2}
                                className={`${getHeaderClassesWithCustomColors(tableClasses.headerCell, gridConfig.headerCustomization || {})} ${(isDraggable || header?.column.getCanSort()) ? 'cursor-pointer' : ''
                                  } ${isDraggable ? 'draggable-column' : ''
                                  } ${gridConfig.enableColumnResizing ? 'relative overflow-visible' : ''} ${draggedColumn === item.column.field ? 'opacity-50' : ''
                                  }`}
                                style={{
                                  width: header.getSize(),
                                  minWidth: Math.max(header.getSize(), 120),
                                  maxWidth: gridConfig.enableColumnResizing ? header.getSize() : undefined,
                                }}
                                draggable={!!isDraggable}
                                onDragStart={(e) => columnConfig?.field && handleColumnDragStart(e, columnConfig.field)}
                                onDragOver={gridConfig.columnReordering ? handleColumnDragOver : undefined}
                                onDrop={gridConfig.columnReordering ? (e) => handleColumnDrop(e, item.column.field) : undefined}
                                onClick={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const clickX = e.clientX;
                                  const rightEdge = rect.right;
                                  const isResizeArea = clickX > rightEdge - 10;
                                  if (!isResizeArea) {
                                    if (header?.column.getCanSort()) {
                                      const sortHandler = header.column.getToggleSortingHandler();
                                      if (sortHandler) sortHandler(e);
                                    }
                                  }
                                }}
                              >
                                <div className="flex flex-col items-center justify-center min-w-0">
                                  <div className="flex items-center justify-center w-full">
                                    <span className="whitespace-normal break-words leading-tight">
                                      {flexRender(header.column.columnDef.header, header.getContext())}
                                    </span>
                                    {columnConfig?.columnType !== 'button' && (
                                      <div className="ml-1 flex-shrink-0">
                                        <i className="ri-expand-up-down-line w-3 h-3 flex items-center justify-center text-gray-400"></i>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {gridConfig.enableColumnNumbering && (
                                  <div className="mt-1">
                                    <span className="inline-flex items-center justify-center w-4 h-4 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs font-bold rounded-full">
                                      {leafOrderMap.get(singleKey) ?? 0}
                                    </span>
                                  </div>
                                )}
                                {gridConfig.enableColumnResizing && header && (
                                  <div
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                    }}
                                    onMouseDown={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      const resizeHandler = header.getResizeHandler();
                                      if (resizeHandler) resizeHandler(e);
                                    }}
                                    onTouchStart={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      const resizeHandler = header.getResizeHandler();
                                      if (resizeHandler) resizeHandler(e);
                                    }}
                                    className={`absolute -right-1 top-0 w-3 cursor-col-resize select-none touch-none hover:bg-blue-500 transition-colors duration-200 ${header.column.getIsResizing() ? 'bg-blue-500' : 'bg-transparent hover:bg-blue-300'
                                      }`}
                                    style={{
                                      height: '100%',
                                      zIndex: 20,
                                      borderRight: '2px solid transparent',
                                    }}
                                    onMouseEnter={(e) => {
                                      (e.currentTarget as HTMLElement).style.borderRight = '2px solid #3b82f6';
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!header.column.getIsResizing()) {
                                        (e.currentTarget as HTMLElement).style.borderRight = '2px solid transparent';
                                      }
                                    }}
                                    title="Drag to resize column"
                                  />
                                )}
                              </th>
                            );
                          }
                          return null;
                        })}
                      </tr>
                    );

                    // Level 3: Individual columns
                    rows.push(
                      <tr key="level-3" className={tableClasses.headerRow}>
                        {headerStructure.map((item, index) => {
                          if (item.type === 'mainGroup') {
                            // Render columns from subgroups
                            return item.subGroups.flatMap((subGroup: any, subIndex: number) => {
                              const isSingleLeaf = subGroup.columns.length === 1 && !subGroup.columns[0]?.subColumns;
                              if (isSingleLeaf) {
                                // Already rendered at level-2 with rowSpan=2
                                return [] as any[];
                              }
                              return subGroup.columns.map((col: any, colIndex: number) => {
                                const colKey = leafKey(col);
                                const header = dataHeaders.find(h => h.column.id === colKey);
                                const columnConfig = flattenedColumns.find(f => (f.field || f.columnName) === colKey);
                                const isGroupable = columnConfig?.allowGrouping !== false && columnConfig?.field && !groupedColumns.includes(columnConfig.field);
                                const isReorderable = gridConfig.columnReordering && columnConfig?.field;
                                const isDraggable = isGroupable || isReorderable;

                                const subColWidth = header?.getSize() || columnConfig?.width || 'auto';
                                return (
                                  <th
                                    key={`col-${index}-${subIndex}-${colIndex}`}
                                    className={`${getHeaderClassesWithCustomColors(tableClasses.headerCell, gridConfig.headerCustomization || {})} ${(isDraggable || header?.column.getCanSort()) ? 'cursor-pointer' : ''
                                      } ${isDraggable ? 'draggable-column' : ''
                                      } ${gridConfig.enableColumnResizing ? 'relative overflow-visible' : ''} ${draggedColumn === col.field ? 'opacity-50' : ''
                                      } ${col.column_css_class || getGroupColor(index, 'sub')}`}
                                    style={{
                                      width: subColWidth,
                                      minWidth: subColWidth,
                                      maxWidth: subColWidth,
                                    }}
                                    draggable={!!isDraggable}
                                    onDragStart={(e) => columnConfig?.field && handleColumnDragStart(e, columnConfig.field)}
                                    onDragOver={gridConfig.columnReordering ? handleColumnDragOver : undefined}
                                    onDrop={gridConfig.columnReordering ? (e) => handleColumnDrop(e, col.field) : undefined}
                                    onClick={(e) => {
                                      // Prevent sorting when clicking on resize handle area
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const clickX = e.clientX;
                                      const rightEdge = rect.right;
                                      const isResizeArea = clickX > rightEdge - 10;
                                      if (!isResizeArea && header?.column.getCanSort()) {
                                        const sortHandler = header.column.getToggleSortingHandler();
                                        if (sortHandler) sortHandler(e);
                                      }
                                    }}
                                  >
                                    <div className="flex flex-col items-center justify-center min-w-0">
                                      <div className="flex items-center justify-center w-full">
                                        <span className="text-center flex-1 whitespace-normal break-words leading-tight">
                                          {header ? flexRender(header.column.columnDef.header, header.getContext()) : (col.columnName || col.field)}
                                        </span>
                                        {columnConfig?.columnType !== 'button' && (
                                          <div className="ml-1 flex-shrink-0">
                                            <i className="ri-expand-up-down-line w-3 h-3 flex items-center justify-center text-gray-400"></i>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {gridConfig.enableColumnNumbering && (
                                      <div className="mt-1">
                                        <span className="inline-flex items-center justify-center w-4 h-4 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs font-bold rounded-full">
                                          {leafOrderMap.get(colKey) ?? 0}
                                        </span>
                                      </div>
                                    )}
                                    {gridConfig.enableColumnResizing && header && (
                                      <div
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                        }}
                                        onMouseDown={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          const resizeHandler = header.getResizeHandler();
                                          if (resizeHandler) resizeHandler(e);
                                        }}
                                        onTouchStart={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          const resizeHandler = header.getResizeHandler();
                                          if (resizeHandler) resizeHandler(e);
                                        }}
                                        className={`absolute -right-1 top-0 w-3 cursor-col-resize select-none touch-none hover:bg-blue-500 transition-colors duration-200 ${header.column.getIsResizing() ? 'bg-blue-500' : 'bg-transparent hover:bg-blue-300'
                                          }`}
                                        style={{
                                          height: '100%',
                                          zIndex: 20,
                                          borderRight: '2px solid transparent',
                                        }}
                                        onMouseEnter={(e) => {
                                          (e.currentTarget as HTMLElement).style.borderRight = '2px solid #3b82f6';
                                        }}
                                        onMouseLeave={(e) => {
                                          if (!header.column.getIsResizing()) {
                                            (e.currentTarget as HTMLElement).style.borderRight = '2px solid transparent';
                                          }
                                        }}
                                        title="Drag to resize column"
                                      />
                                    )}
                                  </th>
                                );
                              });
                            });
                          } else if (item.type === 'group') {
                            // Regular group columns
                            return item.columns.map((col: any, colIndex: number) => {
                              const header = dataHeaders.find(h => h.column.id === col.field);
                              const columnConfig = flattenedColumns.find(f => f.field === col.field);
                              const isGroupable = columnConfig?.allowGrouping !== false && columnConfig?.field && !groupedColumns.includes(columnConfig.field);
                              const isReorderable = gridConfig.columnReordering && columnConfig?.field;
                              const isDraggable = isGroupable || isReorderable;

                              const subColWidth = header?.getSize() || columnConfig?.width || 'auto';
                              return (
                                <th
                                  key={`group-col-${index}-${colIndex}`}
                                  className={`${getHeaderClassesWithCustomColors(tableClasses.headerCell, gridConfig.headerCustomization || {})} ${(isDraggable || header?.column.getCanSort()) ? 'cursor-pointer' : ''
                                    } ${isDraggable ? 'draggable-column' : ''
                                    } ${gridConfig.enableColumnResizing ? 'relative overflow-visible' : ''} ${draggedColumn === col.field ? 'opacity-50' : ''
                                    } ${col.column_css_class || getGroupColor(index, 'sub')}`}
                                  style={{
                                    width: subColWidth,
                                    minWidth: subColWidth,
                                    maxWidth: subColWidth,
                                  }}
                                  draggable={!!isDraggable}
                                  onDragStart={(e) => columnConfig?.field && handleColumnDragStart(e, columnConfig.field)}
                                  onDragOver={gridConfig.columnReordering ? handleColumnDragOver : undefined}
                                  onDrop={gridConfig.columnReordering ? (e) => handleColumnDrop(e, col.field) : undefined}
                                  onClick={(e) => {
                                    // Prevent sorting when clicking on resize handle area
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const clickX = e.clientX;
                                    const rightEdge = rect.right;
                                    const isResizeArea = clickX > rightEdge - 10;
                                    if (!isResizeArea && header?.column.getCanSort()) {
                                      const sortHandler = header.column.getToggleSortingHandler();
                                      if (sortHandler) sortHandler(e);
                                    }
                                  }}
                                >
                                  <div className="flex flex-col items-center justify-center min-w-0">
                                    <div className="flex items-center justify-center w-full">
                                      <span className="text-center flex-1 whitespace-normal break-words leading-tight">
                                        {header ? flexRender(header.column.columnDef.header, header.getContext()) : (col.columnName || col.field)}
                                      </span>
                                      {columnConfig?.columnType !== 'button' && (
                                        <div className="ml-1 flex-shrink-0">
                                          <i className="ri-expand-up-down-line w-3 h-3 flex items-center justify-center text-gray-400"></i>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {gridConfig.enableColumnResizing && header && (
                                    <div
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                      }}
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        const resizeHandler = header.getResizeHandler();
                                        if (resizeHandler) resizeHandler(e);
                                      }}
                                      onTouchStart={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        const resizeHandler = header.getResizeHandler();
                                        if (resizeHandler) resizeHandler(e);
                                      }}
                                      className={`absolute -right-1 top-0 w-3 cursor-col-resize select-none touch-none hover:bg-blue-500 transition-colors duration-200 ${header.column.getIsResizing() ? 'bg-blue-500' : 'bg-transparent hover:bg-blue-300'
                                        }`}
                                      style={{
                                        height: '100%',
                                        zIndex: 20,
                                        borderRight: '2px solid transparent',
                                      }}
                                      onMouseEnter={(e) => {
                                        (e.currentTarget as HTMLElement).style.borderRight = '2px solid #3b82f6';
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!header.column.getIsResizing()) {
                                          (e.currentTarget as HTMLElement).style.borderRight = '2px solid transparent';
                                        }
                                      }}
                                      title="Drag to resize column"
                                    />
                                  )}
                                </th>
                              );
                            });
                          }
                          // Single columns already handled with rowSpan
                          return null;
                        })}
                      </tr>
                    );

                    return rows;
                  } else if (gridConfig.enableColumnGrouping && gridConfig.columnGroups && gridConfig.columnGroups.length > 0) {
                    // New column grouping system - conditionally handle selection column
                    const allHeaders = table.getHeaderGroups()[0].headers;
                    const selectionHeader = allHeaders.find(h => h.column.id === 'select') || null;

                    const headerStructure = createGroupedColumnStructure(columns, gridConfig.columnGroups);

                    return (
                      <>
                        {/* Group header row */}
                        <tr className={tableClasses.headerRow}>
                          {/* Selection header - render with rowSpan=2 */}
                          {selectionHeader && (
                            <th
                              key="select-header"
                              rowSpan={2}
                              className={`${getHeaderClassesWithCustomColors(tableClasses.headerCell, gridConfig.headerCustomization || {})} cursor-pointer text-center`}
                              style={{
                                width: selectionHeader.getSize(),
                                minWidth: 50,
                                maxWidth: gridConfig.enableColumnResizing ? selectionHeader.getSize() : undefined,
                              }}
                            >
                              {flexRender(selectionHeader.column.columnDef.header, selectionHeader.getContext())}
                            </th>
                          )}

                          {headerStructure.groupHeaders.map((groupHeader) => {
                            const colspan = getNewGroupColumnSpan(groupHeader.id, headerStructure.columnHeaders);
                            return (
                              <th
                                key={`group-${groupHeader.id}`}
                                colSpan={colspan}
                                className={`${tableClasses.groupHeaderCell || tableClasses.headerCell} ${groupHeader.cssClass || ''} text-center font-semibold`}
                                style={{
                                  textAlign: groupHeader.alignment || 'center',
                                  width: groupHeader.width
                                }}
                              >
                                {groupHeader.name}
                              </th>
                            );
                          })}
                          {/* Render ungrouped column headers inline with rowSpan=2 */}
                          {headerStructure.columnHeaders.filter(h => !h.groupId).map((columnHeader) => {
                            const columnConfig = columnHeader.columnConfig;
                            const isGroupable = columnConfig?.allowGrouping !== false && columnConfig?.field && !groupedColumns.includes(columnConfig.field);
                            const isReorderable = gridConfig.columnReordering && columnConfig?.field;
                            const isDraggable = isGroupable || isReorderable;

                            const header = table.getHeaderGroups()[0]?.headers.find(h => h.column.id === columnHeader.field);
                            const canSort = header?.column.getCanSort();

                            return (
                              <th
                                key={`ungrouped-${columnHeader.field}`}
                                rowSpan={2}
                                className={`${tableClasses.headerCell} ${(isDraggable || canSort) ? 'cursor-pointer' : ''
                                  } ${isDraggable ? 'draggable-column' : ''
                                  } ${gridConfig.enableColumnResizing ? 'relative overflow-visible' : ''} ${draggedColumn === columnHeader.field ? 'opacity-50' : ''
                                  }`}
                                style={{
                                  width: columnConfig.width || 'auto',
                                  minWidth: gridConfig.enableColumnResizing ? columnConfig.width : undefined,
                                  maxWidth: gridConfig.enableColumnResizing ? columnConfig.width : undefined,
                                }}
                                draggable={!!isDraggable}
                                onDragStart={(e) => columnConfig?.field && handleColumnDragStart(e, columnConfig.field)}
                                onDragOver={gridConfig.columnReordering ? handleColumnDragOver : undefined}
                                onDrop={gridConfig.columnReordering ? (e) => handleColumnDrop(e, columnHeader.field) : undefined}
                                onClick={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const clickX = e.clientX;
                                  const rightEdge = rect.right;
                                  const isResizeArea = clickX > rightEdge - 10;
                                  if (!isResizeArea) {
                                    const header = table.getHeaderGroups()[0]?.headers.find(h => h.column.id === columnHeader.field);
                                    if (header?.column.getCanSort()) {
                                      const sortHandler = header.column.getToggleSortingHandler();
                                      if (sortHandler) {
                                        sortHandler(e);
                                      }
                                    }
                                  }
                                }}
                              >
                                <div className="flex flex-col items-center justify-center min-w-0">
                                  <div className="flex items-center justify-center w-full">
                                    <span className="text-center flex-1 whitespace-normal break-words leading-tight">
                                      {columnHeader.columnName}
                                    </span>
                                    {columnConfig?.columnType !== 'button' && (
                                      <div className="ml-1 flex-shrink-0">
                                        <i className="ri-expand-up-down-line w-3 h-3 flex items-center justify-center text-gray-400"></i>
                                      </div>
                                    )}
                                  </div>
                                  {gridConfig.enableColumnNumbering && (
                                    <div className="mt-1">
                                      <span className="inline-flex items-center justify-center w-4 h-4 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs font-bold rounded-full">
                                        {headerStructure.columnHeaders.findIndex(h => h.field === columnHeader.field) + 1}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                        {/* Column header row */}
                        <tr className={tableClasses.headerRow}>
                          {headerStructure.columnHeaders.filter(h => !!h.groupId).map((columnHeader) => {
                            const columnConfig = columnHeader.columnConfig;
                            const isGroupable = columnConfig?.allowGrouping !== false && columnConfig?.field && !groupedColumns.includes(columnConfig.field);
                            const isReorderable = gridConfig.columnReordering && columnConfig?.field;
                            const isDraggable = isGroupable || isReorderable;

                            const header = table.getHeaderGroups()[0]?.headers.find(h => h.column.id === columnHeader.field);
                            const canSort = header?.column.getCanSort();

                            return (
                              <th
                                key={`column-${columnHeader.field}`}
                                className={`${tableClasses.headerCell} ${(isDraggable || canSort) ? 'cursor-pointer' : ''
                                  } ${isDraggable ? 'draggable-column' : ''
                                  } ${gridConfig.enableColumnResizing ? 'relative overflow-visible' : ''} ${draggedColumn === columnHeader.field ? 'opacity-50' : ''
                                  }`}
                                style={{
                                  width: columnConfig.width || 'auto',
                                  minWidth: gridConfig.enableColumnResizing ? columnConfig.width : undefined,
                                  maxWidth: gridConfig.enableColumnResizing ? columnConfig.width : undefined,
                                }}
                                draggable={!!isDraggable}
                                onDragStart={(e) => columnConfig?.field && handleColumnDragStart(e, columnConfig.field)}
                                onDragOver={gridConfig.columnReordering ? handleColumnDragOver : undefined}
                                onDrop={gridConfig.columnReordering ? (e) => handleColumnDrop(e, columnHeader.field) : undefined}
                                onClick={(e) => {
                                  // Check if click is on resize handle area
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const clickX = e.clientX;
                                  const rightEdge = rect.right;
                                  const isResizeArea = clickX > rightEdge - 10; // 10px from right edge

                                  if (!isResizeArea) {
                                    // Handle sorting if supported
                                    const header = table.getHeaderGroups()[0]?.headers.find(h => h.column.id === columnHeader.field);
                                    if (header?.column.getCanSort()) {
                                      const sortHandler = header.column.getToggleSortingHandler();
                                      if (sortHandler) {
                                        sortHandler(e);
                                      }
                                    }
                                  }
                                }}
                              >
                                <div className="flex flex-col items-center justify-center min-w-0">
                                  <div className="flex items-center justify-center w-full">
                                    <span className="text-center flex-1 whitespace-normal break-words leading-tight">
                                      {columnHeader.columnName}
                                    </span>
                                    {columnConfig?.columnType !== 'button' && (
                                      <div className="ml-1 flex-shrink-0">
                                        <i className="ri-expand-up-down-line w-3 h-3 flex items-center justify-center text-gray-400"></i>
                                      </div>
                                    )}
                                  </div>
                                  {gridConfig.enableColumnNumbering && (
                                    <div className="mt-1">
                                      <span className="inline-flex items-center justify-center w-4 h-4 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs font-bold rounded-full">
                                        {headerStructure.columnHeaders.findIndex(h => h.field === columnHeader.field) + 1}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </>
                    );
                  } else if (gridConfig.enableMultiLevelHeaders && gridConfig.defaultColumnGroups && gridConfig.defaultColumnGroups.length > 0) {
                    // Legacy multi-level header rendering
                    const headerStructure = createMultiLevelHeaderStructure(columns, gridConfig.defaultColumnGroups);

                    return (
                      <>
                        {/* Group header row */}
                        <tr className={tableClasses.headerRow}>
                          {headerStructure.groupHeaders.map((groupHeader) => {
                            const colspan = getGroupColumnSpan(groupHeader.groupName, headerStructure.columnHeaders);
                            return (
                              <th
                                key={`group-${groupHeader.groupName}`}
                                colSpan={colspan}
                                className={`${tableClasses.groupHeaderCell || tableClasses.headerCell} ${groupHeader.cssClass || ''} text-center font-semibold`}
                                style={{
                                  textAlign: groupHeader.alignment || 'center',
                                  width: groupHeader.width
                                }}
                              >
                                {groupHeader.groupLabel}
                              </th>
                            );
                          })}
                          {/* Add empty cells for ungrouped columns */}
                          {headerStructure.columnHeaders.filter(h => !h.groupName).map((_, index) => (
                            <th key={`ungrouped-${index}`} className={tableClasses.headerCell}></th>
                          ))}
                        </tr>
                        {/* Column header row */}
                        <tr className={tableClasses.headerRow}>
                          {headerStructure.columnHeaders.map((columnHeader) => {
                            const columnConfig = columnHeader.columnConfig;
                            const isGroupable = columnConfig?.allowGrouping !== false && columnConfig?.field && !groupedColumns.includes(columnConfig.field);
                            const isReorderable = gridConfig.columnReordering && columnConfig?.field;
                            const isDraggable = isGroupable || isReorderable;

                            const header = table.getHeaderGroups()[0]?.headers.find(h => h.column.id === columnHeader.field);
                            const canSort = header?.column.getCanSort();

                            return (
                              <th
                                key={`column-${columnHeader.field}`}
                                className={`${tableClasses.headerCell} ${(isDraggable || canSort) ? 'cursor-pointer' : ''
                                  } ${isDraggable ? 'draggable-column' : ''
                                  } ${gridConfig.enableColumnResizing ? 'relative overflow-visible' : ''} ${draggedColumn === columnHeader.field ? 'opacity-50' : ''
                                  }`}
                                style={{
                                  width: columnConfig.width || 'auto',
                                  minWidth: gridConfig.enableColumnResizing ? columnConfig.width : undefined,
                                  maxWidth: gridConfig.enableColumnResizing ? columnConfig.width : undefined,
                                }}
                                draggable={!!isDraggable}
                                onDragStart={(e) => columnConfig?.field && handleColumnDragStart(e, columnConfig.field)}
                                onDragOver={gridConfig.columnReordering ? handleColumnDragOver : undefined}
                                onDrop={gridConfig.columnReordering ? (e) => handleColumnDrop(e, columnHeader.field) : undefined}
                                onClick={(e) => {
                                  // Check if click is on resize handle area
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const clickX = e.clientX;
                                  const rightEdge = rect.right;
                                  const isResizeArea = clickX > rightEdge - 10; // 10px from right edge

                                  if (!isResizeArea) {
                                    // Handle sorting if supported
                                    const header = table.getHeaderGroups()[0]?.headers.find(h => h.column.id === columnHeader.field);
                                    if (header?.column.getCanSort()) {
                                      const sortHandler = header.column.getToggleSortingHandler();
                                      if (sortHandler) {
                                        sortHandler(e);
                                      }
                                    }
                                  }
                                }}
                              >
                                <div className="flex flex-col items-center justify-center min-w-0">
                                  <div className="flex items-center justify-center w-full">
                                    <span className="text-center flex-1 whitespace-normal break-words leading-tight">
                                      {columnHeader.columnName}
                                    </span>
                                    {columnConfig?.columnType !== 'button' && (
                                      <div className="ml-1 flex-shrink-0">
                                        <i className="ri-expand-up-down-line w-3 h-3 flex items-center justify-center text-gray-400"></i>
                                      </div>
                                    )}
                                  </div>
                                  {gridConfig.enableColumnNumbering && (
                                    <div className="mt-1">
                                      <span className="inline-flex items-center justify-center w-4 h-4 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs font-bold rounded-full">
                                        {headerStructure.columnHeaders.findIndex(h => h.field === columnHeader.field) + 1}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </>
                    );
                  } else {
                    // Standard single-level header rendering
                    return table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id} className={tableClasses.headerRow}>
                        {headerGroup.headers.map((header, headerIndex) => {
                          const columnConfig = columns.find((col) => (col.field || col.columnName) === header.column.id);
                          const isGroupable = columnConfig?.allowGrouping !== false && columnConfig?.field && !groupedColumns.includes(columnConfig.field);
                          const isReorderable = gridConfig.columnReordering && columnConfig?.field;
                          const isDraggable = isGroupable || isReorderable;

                          // Check if this is a serial number column (first data column, excluding selection)
                          const dataColumnIndex = header.column.id === 'select' ? -1 : headerIndex - (headerGroup.headers[0]?.column.id === 'select' ? 1 : 0);
                          const isSerial = isSerialNumberColumn(columnConfig?.columnName || '', columnConfig?.field || '', dataColumnIndex);
                          const isActions = isActionsColumn(columnConfig?.columnName || '', columnConfig?.field || '');
                          const serialStyle = getSerialColumnStyle(isSerial);
                          // For actions columns, prefer an explicit `width` from columnConfig
                          // so callers can widen the column beyond the 100px default when they
                          // render multiple buttons.
                          const actionsColW = columnConfig?.width || ACTIONS_COL_WIDTH;
                          const actionsStyle = isActions
                            ? { width: actionsColW, minWidth: actionsColW, maxWidth: actionsColW, textAlign: 'center' as const }
                            : {};
                          const skipFilter = isSerial || isActions;

                          // Determine column width based on type
                          const getColumnWidth = () => {
                            if (isSerial) return SERIAL_COL_WIDTH;
                            if (isActions) return actionsColW;
                            return header.getSize();
                          };
                          const columnWidth = getColumnWidth();

                          return (
                            <th
                              key={header.id}
                              className={`${getHeaderClassesWithCustomColors(tableClasses.headerCell, gridConfig.headerCustomization || {})} ${(isDraggable || header?.column.getCanSort()) ? 'cursor-pointer' : ''
                                } ${isDraggable ? 'draggable-column' : ''
                                } ${gridConfig.enableColumnResizing ? 'relative' : ''} ${draggedColumn === header.id ? 'opacity-50' : ''
                                } ${columnConfig?.headerClass || ''}`}
                              style={{
                                width: columnWidth,
                                minWidth: isSerial ? SERIAL_COL_WIDTH : (isActions ? actionsColW : 80),
                                maxWidth: (isSerial || isActions) ? columnWidth : (gridConfig.enableColumnResizing ? header.getSize() : undefined),
                                overflow: 'hidden',
                                ...serialStyle,
                                ...actionsStyle,
                              }}
                              draggable={!!isDraggable}
                              onDragStart={(e) => columnConfig?.field && handleColumnDragStart(e, columnConfig.field)}
                              onDragOver={gridConfig.columnReordering ? handleColumnDragOver : undefined}
                              onDrop={gridConfig.columnReordering ? (e) => handleColumnDrop(e, header.id) : undefined}
                              onClick={(e) => {
                                // Check if click is on resize handle area
                                const rect = e.currentTarget.getBoundingClientRect();
                                const clickX = e.clientX;
                                const rightEdge = rect.right;
                                const isResizeArea = clickX > rightEdge - 10; // 10px from right edge

                                if (!isResizeArea && header.column.getCanSort()) {
                                  const sortHandler = header.column.getToggleSortingHandler();
                                  if (sortHandler) {
                                    sortHandler(e);
                                  }
                                }
                              }}
                            >
                              <div className="flex flex-col items-center justify-center min-w-0">
                                <div className="flex items-center justify-center w-full">
                                  <span
                                    className="whitespace-normal break-words leading-tight text-center flex-1"
                                    title={header.isPlaceholder ? undefined : String(flexRender(header.column.columnDef.header, header.getContext()))}
                                  >
                                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                    {columnConfig?.parentId && columnConfig.parentId !== 'None' && (
                                      <span className="text-red-500 font-bold ml-1 text-[11px] block">[PID: {columnConfig.parentId}]</span>
                                    )}
                                  </span>
                                  {header.column.getCanSort() && (
                                    <div className="ml-1 flex-shrink-0">
                                      {header.column.getIsSorted() === 'asc' ? (
                                        <i className="ri-arrow-up-line w-3 h-3 flex items-center justify-center"></i>
                                      ) : header.column.getIsSorted() === 'desc' ? (
                                        <i className="ri-arrow-down-line w-3 h-3 flex items-center justify-center"></i>
                                      ) : (
                                        <i className="ri-expand-up-down-line w-3 h-3 flex items-center justify-center text-gray-400"></i>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {gridConfig.enableColumnNumbering && (
                                  <div className="mt-1">
                                    <span className="inline-flex items-center justify-center w-4 h-4 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs font-bold rounded-full">
                                      {headerGroup.headers.findIndex(h => h.id === header.id) + 1}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {gridConfig.enableColumnFilters && header.column.getCanFilter() && !skipFilter && columnConfig?.allowAdvancedFilter && (
                                <div className="mt-2 w-full overflow-hidden">
                                  <AdvancedFilter
                                    key={`${header.id}-${header.getSize()}`}
                                    value={header.column.getFilterValue()}
                                    onChange={(value) => setColumnFilter(header.column.id, value)}
                                    placeholder={`Filter...`}
                                    columnType={columnConfig?.columnType || 'text'}
                                    width={header.getSize()}
                                    originalWidth={columnConfig?.width ? parseInt(columnConfig.width) : undefined}
                                  />
                                </div>
                              )}
                              {gridConfig.enableColumnFilters && header.column.getCanFilter() && !skipFilter && !columnConfig?.allowAdvancedFilter && (() => {
                                const colField = columnConfig?.field || header.column.id;
                                const useDropdown = shouldUseDropdownFilter(colField);
                                const currentFilter = table.getState().columnFilters.find(f => f.id === header.column.id);
                                const filterVal = currentFilter?.value;

                                if (useDropdown) {
                                  return (
                                    <div className="mt-2 w-full overflow-hidden">
                                      <FilterDropdown
                                        data={dataSource}
                                        field={colField}
                                        filterField={columnConfig?.filterField}
                                        filterValue={columnConfig?.filterValue}
                                        value={Array.isArray(filterVal) ? filterVal : []}
                                        onChange={(selected) => setColumnFilter(header.column.id, selected)}
                                        placeholder="Filter..."
                                      />
                                    </div>
                                  );
                                }
                                return (
                                  <div className="mt-2 w-full overflow-hidden">
                                    <div className="relative w-full">
                                      <input
                                        type="text"
                                        value={typeof filterVal === 'string' ? filterVal : ''}
                                        onChange={(e) => {
                                          setColumnFilter(header.column.id, e.target.value || undefined);
                                        }}
                                        placeholder="Type to filter..."
                                        className="w-full px-2 pr-6 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 overflow-hidden text-ellipsis"
                                        onClick={(e) => e.stopPropagation()}
                                        onFocus={(e) => e.stopPropagation()}
                                      />
                                      <i className={`ri-funnel-line absolute right-1 top-1/2 transform -translate-y-1/2 w-3 h-3 pointer-events-none ${filterVal ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}></i>
                                    </div>
                                  </div>
                                );
                              })()}
                              {/* Column Resize Handle */}
                              {gridConfig.enableColumnResizing && (
                                <div
                                  onClick={(e) => {
                                    // console.log('Resize handle clicked (onClick):', header.column.id);
                                    e.stopPropagation(); // Prevent sorting
                                    e.preventDefault(); // Prevent default behavior
                                  }}
                                  onMouseDown={(e) => {
                                    // console.log('Resize handle clicked for column:', header.column.id);
                                    e.stopPropagation(); // Prevent sorting
                                    e.preventDefault(); // Prevent default behavior
                                    const resizeHandler = header.getResizeHandler();
                                    if (resizeHandler) {
                                      resizeHandler(e);
                                    }
                                  }}
                                  onTouchStart={(e) => {
                                    e.stopPropagation(); // Prevent sorting
                                    e.preventDefault(); // Prevent default behavior
                                    const resizeHandler = header.getResizeHandler();
                                    if (resizeHandler) {
                                      resizeHandler(e);
                                    }
                                  }}
                                  className={`absolute -right-1 top-0 w-3 cursor-col-resize select-none touch-none hover:bg-blue-500 transition-colors duration-200 ${header.column.getIsResizing() ? 'bg-blue-500' : 'bg-transparent hover:bg-blue-300'
                                    }`}
                                  style={{
                                    height: '100%',
                                    zIndex: 20,
                                    borderRight: '2px solid transparent',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.borderRight = '2px solid #3b82f6';
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!header.column.getIsResizing()) {
                                      e.currentTarget.style.borderRight = '2px solid transparent';
                                    }
                                  }}
                                  title="Drag to resize column"
                                />
                              )}
                            </th>
                          );
                        })}
                      </tr>
                    ));
                  }
                })()}
              </thead>

              <tbody className={tableClasses.tbody}>
                {groupedColumns.length > 0 && groupedData ? (
                  renderGroupedRows(groupedData, flattenedColumns, table, 0, '', props.onRowClick)
                ) : (
                  table.getRowModel().rows.map((row, index) => {
                    const isDetailExpanded = props.renderDetailRow && props.expandedRowId != null && String(row.original.id ?? row.original.wrkflw_inst_id ?? index) === String(props.expandedRowId);
                    return (
                    <React.Fragment key={row.id}>
                    <tr
                      className={`${tableClasses.bodyRow} ${props.onRowClick ? 'cursor-pointer' : ''
                        } ${index % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800'
                        } hover:bg-blue-50 transition-colors ${props.getRowClassName?.(row.original) || ''}`}
                      onClick={(e) => {
                        // Don't trigger row click if clicking on checkbox (select column)
                        const target = e.target as HTMLElement;
                        if (target.tagName === 'INPUT' && target.getAttribute('type') === 'checkbox') {
                          return;
                        }
                        // Also check if click is within a cell that contains a checkbox
                        const closestCell = target.closest('td');
                        if (closestCell?.querySelector('input[type="checkbox"]') && target.tagName !== 'TD') {
                          return;
                        }
                        props.onRowClick?.(row.original, e);
                      }}
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => {
                        // Check if this is a serial number or actions column
                        const columnConfig = flattenedColumns.find((col) => (col.field || col.columnName) === cell.column.id);
                        const dataCellIndex = cell.column.id === 'select' ? -1 : cellIndex - (row.getVisibleCells()[0]?.column.id === 'select' ? 1 : 0);
                        const isSerial = isSerialNumberColumn(columnConfig?.columnName || '', columnConfig?.field || '', dataCellIndex);
                        const isActions = isActionsColumn(columnConfig?.columnName || '', columnConfig?.field || '');

                        // Determine cell style based on column type and alignment
                        const getCellStyle = () => {
                          if (isSerial) return { textAlign: 'center' as const, width: SERIAL_COL_WIDTH, minWidth: SERIAL_COL_WIDTH, maxWidth: SERIAL_COL_WIDTH };
                          if (isActions) {
                            // Respect an explicit column width if provided — otherwise fall back
                            // to the default. Without this, a column with multiple buttons gets
                            // clipped because ACTIONS_COL_WIDTH is only 100px.
                            const w = columnConfig?.width || ACTIONS_COL_WIDTH;
                            return { textAlign: 'center' as const, width: w, minWidth: w, maxWidth: w };
                          }
                          // Apply column alignment
                          if (columnConfig?.alignment) {
                            return { textAlign: columnConfig.alignment as 'left' | 'center' | 'right' };
                          }
                          return undefined;
                        };

                        // Determine alignment class for the wrapper div
                        const getAlignmentClass = () => {
                          if (isSerial || isActions) return 'text-center';
                          if (columnConfig?.alignment === 'right') return 'text-right';
                          if (columnConfig?.alignment === 'center') return 'text-center';
                          return '';
                        };

                        return (
                          <td
                            key={cell.id}
                            className={`${tableClasses.bodyCell} overflow-hidden ${columnConfig?.cellClass || ''}`}
                            style={{
                              ...getCellStyle(),
                              maxWidth: columnConfig?.width || '200px',
                            }}
                          >
                            <div className={`truncate ${getAlignmentClass()}`} title={typeof cell.getValue() === 'string' ? cell.getValue() as string : undefined}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    {isDetailExpanded && (
                      <tr className="bg-slate-50">
                        <td colSpan={row.getVisibleCells().length} className="p-0">
                          {props.renderDetailRow!(row.original)}
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  }))
                }
              </tbody>

              {/* Summary Row */}
              {props.columns.some((col: any) => col.summaryType) && groupedColumns.length === 0 && (
                <SummaryRow table={table} columns={props.columns} data={table.getFilteredRowModel().rows.map(r => r.original)} />
              )}

              {/* Optional caller-supplied summary / totals row — rendered inside <table> so cells align with the header columns */}
              {props.customFooter}
            </table>
          </div>

          {/* Table Footer with Pagination - Fixed at bottom */}
          {gridConfig.pagination && (
            <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-600 px-2 sm:px-4 py-2 sm:py-3">
              <TablePagination table={table} gridConfig={gridConfig} />
            </div>
          )}
        </div>
      </div>);
  };

  return (
    <div className="h-full flex flex-col min-h-0" style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', overflowY: 'hidden' }}>
      {gridConfig.title && (
        <div className="mb-2 flex-shrink-0 flex items-center">
          <h2 className="text-xl sm:text-2xl font-bold pl-[14px] text-gray-900 dark:text-gray-100">{gridConfig.title}</h2>
        </div>
      )}

      {/* A flex column (and the table view below a flex-1 item of it), not just
          `h-full` all the way down: under a host that only caps the height
          (max-h), `h-full` resolves to auto, so the cap never reached the
          scrolling body and the grid was clipped instead of scrolling. */}
      <div className="flex-1 min-h-0 flex flex-col" style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', overflowY: 'hidden' }}>
        {renderView()}
      </div>

      <style>{`
        .draggable-column {
          cursor: grab;
        }
        .draggable-column:active {
          cursor: grabbing;
        }
        .draggable-column:hover {
          background-color: #f8fafc;
        }
        .dark .draggable-column:hover {
          background-color: #374151;
        }
      `}</style>
    </div>
  );
};


export default DataGrid;
